import { ResponseType } from "@/api/api";
import { type EnumCompanyType } from "@/api/company";
import { type TDefaultTitleGroup } from "@/api/contact";
// 定义缓存键及其对应的数据
// 缓存较大的数据，默认缓存一天，过期后自动删除
// TChromeCache 类型中的key 直接作为 chrome.storage.local 的key，不要与其他key冲突
// 类型

// 领英原始个人信息缓存
type ProfileKeyType = `lp_${string}` | `lp_o_${string}`;
type LinkedinProfileCache = {
  [key in ProfileKeyType]: any;
};

// 领英原始公司信息缓存
type CompanyKeyType = `lc_${string}` | `lc_o_${string}`;
type LinkedinCompanyCache = { [key in CompanyKeyType]: any };

export type TChromeCache = {
  ExcludedDomainList: ResponseType<EnumCompanyType[]>;
  DefaultTitleGroup: ResponseType<TDefaultTitleGroup[]>;
  // 可以继续添加更多键值对
  // UserProfile: UserProfileType;
  // Settings: SettingsType;
  // 等等...
} & LinkedinProfileCache &
  LinkedinCompanyCache;


// type ChromeCacheItem<K extends keyof TChromeCache> = {
//   key: K;
//   data: TChromeCache[K]; // 这里的联合类型是不精确的
//   config?: CacheConfig;
// };

// 更严格的类型约束，确保每个key对应的data类型完全匹配
// 如果 使用 上面 的 ChromeCacheItem 类型，则 data其实是个联合类型，并不能根据key来完全限定data的类型
type StrictChromeCacheItem = {
  [K in keyof TChromeCache]: {
    key: K;
    data: TChromeCache[K]; // 这里的类型是精确的
    config?: CacheConfig;
  };
}[keyof TChromeCache];

// 缓存项接口
interface CacheItem<T = any> {
  data: T;
  timestamp: number;
  expiresAt: number; // 过期时间戳
  version?: string; // 数据版本
}

// 缓存配置接口
interface CacheConfig {
  ttl?: number; // 生存时间（毫秒）
  version?: string; // 数据版本
  maxSize?: number; // 最大大小（字节）
}

export class ChromeCache {
  private static readonly DEFAULT_TTL = 24 * 60 * 60 * 1000; // 默认1天
  private static readonly MAX_SIZE = 5 * 1024 * 1024; // 默认5MB
  private static readonly MAX_ITEMS = 1000; // 最多1000个缓存项

  /**
   * 设置缓存数据
   */
  static async set<K extends keyof TChromeCache>(
    key: K,
    data: TChromeCache[K],
    config?: CacheConfig
  ): Promise<void> {
    try {
      const now = Date.now();
      const ttl = config?.ttl || this.DEFAULT_TTL;
      const expiresAt = now + ttl;

      const cacheItem: CacheItem<TChromeCache[K]> = {
        data,
        timestamp: now,
        expiresAt,
        version: config?.version,
      };

      // 检查数据大小
      const dataSize = this.calculateSize(cacheItem);
      if (dataSize > (config?.maxSize || this.MAX_SIZE)) {
        throw new Error(`缓存数据过大: ${dataSize} bytes`);
      }

      await this.cleanup();
      await chrome.storage?.local.set({ [key]: cacheItem });
    } catch (error) {
      console.error("ChromeCache.set 失败:", error);
      throw error;
    }
  }

  /**
   * 获取缓存数据
   */
  static async get<K extends keyof TChromeCache>(key: K) {
    try {
      return (await this.getItem(key))?.data;
    } catch (error) {
      console.error("ChromeCache.get 失败:", error);
      throw error;
    }
  }

  /**
   * 获取缓存项（包含元数据）
   */
  static async getItem<K extends keyof TChromeCache>(key: K) {
    try {
      const result = await chrome.storage?.local.get(key);
      const cacheItem = result[key] as CacheItem<TChromeCache[K]>;

      if (!cacheItem) {
        return undefined;
      }

      // 检查是否过期
      if (cacheItem.expiresAt && Date.now() > cacheItem.expiresAt) {
        await this.remove(key);
        return undefined;
      }

      return cacheItem;
    } catch (error) {
      console.error("ChromeCache.getItem 失败:", error);
      throw error;
    }
  }

  /**
   * 删除缓存
   */
  static async remove<K extends keyof TChromeCache>(key: K): Promise<void> {
    try {
      await chrome.storage?.local.remove(key);
    } catch (error) {
      console.error("ChromeCache.remove 失败:", error);
      throw error;
    }
  }

  /**
   * 清空所有缓存
   */
  static async clear(): Promise<void> {
    try {
      const keys = await this.getAllKeys();
      if (keys.length > 0) {
        await chrome.storage?.local.remove(keys);
      }
    } catch (error) {
      console.error("ChromeCache.clear 失败:", error);
      throw error;
    }
  }

  /**
   * 批量设置缓存
   */
  static async setMultiple(items: StrictChromeCacheItem[]): Promise<void> {
    try {
      const promises = items.map(({ key, data, config }) =>
        this.set(key, data, config)
      );
      await Promise.all(promises);
    } catch (error) {
      console.error("ChromeCache.setMultiple 失败:", error);
      throw error;
    }
  }

  /**
   * 批量获取缓存
   */
  static async getMultiple<K extends keyof TChromeCache>(keys: K[]) {
    try {
      const result = await chrome.storage?.local.get(keys);
      const data: Partial<Pick<TChromeCache, K>> = {};

      for (const key of keys) {
        const cacheItem = result[key] as CacheItem<TChromeCache[K]>;

        if (!cacheItem) {
          // 不设置这个键，让它保持 undefined（Partial 类型的默认行为）
          continue;
        }

        // 检查是否过期
        if (cacheItem.expiresAt && Date.now() > cacheItem.expiresAt) {
          await this.remove(key);
          // 不设置这个键，让它保持 undefined
          continue;
        }

        data[key] = cacheItem.data;
      }

      return data;
    } catch (error) {
      console.error("ChromeCache.getMultiple 失败:", error);
      throw error;
    }
  }

  /**
   * 检查缓存是否存在且未过期
   */
  static async has<K extends keyof TChromeCache>(key: K): Promise<boolean> {
    try {
      const data = await this.get(key);
      return data !== undefined;
    } catch (error) {
      console.error("ChromeCache.has 失败:", error);
      return false;
    }
  }

  /**
   * 获取缓存信息
   */
  static async getInfo<K extends keyof TChromeCache>(
    key: K
  ): Promise<
    { size: number; age: number; expiresIn: number | undefined } | undefined
  > {
    try {
      const cacheItem = await this.getItem(key);
      if (!cacheItem) {
        return undefined;
      }

      const now = Date.now();
      const size = this.calculateSize(cacheItem);
      const age = now - cacheItem.timestamp;
      const expiresIn = cacheItem.expiresAt
        ? cacheItem.expiresAt - now
        : undefined;

      return { size, age, expiresIn };
    } catch (error) {
      console.error("ChromeCache.getInfo 失败:", error);
      return undefined;
    }
  }

  /**
   * 清理过期缓存
   */
  static async cleanup(): Promise<number> {
    try {
      // 获取所有缓存键，包括动态生成的键
      const allKeys = await this.getAllKeys();
      if (allKeys.length === 0) {
        return 0;
      }

      const result = (await chrome.storage?.local.get(allKeys)) as Record<
        keyof TChromeCache,
        CacheItem
      >;
      const expiredKeys: Array<keyof TChromeCache> = [];

      for (const key of allKeys) {
        const cacheItem = result[key] as CacheItem;
        if (cacheItem?.expiresAt && Date.now() > cacheItem.expiresAt) {
          expiredKeys.push(key);
        }
      }

      const remainingKeys = allKeys.filter((key) => !expiredKeys.includes(key));
      if (remainingKeys.length > this.MAX_ITEMS) {
        remainingKeys.sort((a, b) => result[a].timestamp - result[b].timestamp);
        expiredKeys.push(
          ...remainingKeys.slice(0, remainingKeys.length - this.MAX_ITEMS)
        );
      }

      if (expiredKeys.length > 0) {
        await chrome.storage?.local.remove(expiredKeys);
      }

      return expiredKeys.length;
    } catch (error) {
      console.error("ChromeCache.cleanup 失败:", error);
      return 0;
    }
  }

  /**
   * 获取所有缓存键
   * 只返回我们定义的缓存键，避免影响其他存储
   */
  static async getAllKeys(): Promise<Array<keyof TChromeCache>> {
    try {
      const result = await chrome.storage?.local.get();
      const keys: Array<keyof TChromeCache> = [];

      // 只检查我们定义的缓存键
      for (const key in result) {
        // 使用类型断言，因为我们只关心我们定义的键
        if (this.isCacheItem(result[key])) {
          keys.push(key as keyof TChromeCache);
        }
      }

      return keys;
    } catch (error) {
      console.error("ChromeCache.getAllKeys 失败:", error);
      return [];
    }
  }

  /**
   * 检查是否是已知的缓存键
   * 这个方法可以根据需要扩展，但只检查我们关心的键
   */
  // private static isKnownCacheKey(key: string): boolean {
  //   // 只检查我们定义的缓存键模式
  //   return (
  //     key.startsWith("lp_") ||
  //     key.startsWith("lc_") ||
  //     key === "ExcludedDomainList" ||
  //     key === "DefaultTitleGroup"
  //   );
  // }

  private static isCacheItem(item: any): boolean {
    return item.expiresAt && item.timestamp;
  }

  /**
   * 获取缓存统计信息
   */
  static async getStats(): Promise<{
    totalItems: number;
    totalSize: number;
    expiredItems: number;
    keys: Array<keyof TChromeCache>;
  }> {
    try {
      const keys = await this.getAllKeys();
      const result = await chrome.storage?.local.get(keys);

      let totalSize = 0;
      let expiredItems = 0;
      const now = Date.now();

      for (const key of keys) {
        const cacheItem = result[key] as CacheItem;
        if (cacheItem) {
          totalSize += this.calculateSize(cacheItem);
          if (cacheItem.expiresAt && now > cacheItem.expiresAt) {
            expiredItems++;
          }
        }
      }

      return {
        totalItems: keys.length,
        totalSize,
        expiredItems,
        keys,
      };
    } catch (error) {
      console.error("ChromeCache.getStats 失败:", error);
      return {
        totalItems: 0,
        totalSize: 0,
        expiredItems: 0,
        keys: [],
      };
    }
  }

  /**
   * 监听缓存变化
   */
  static onChanged(
    callback: (changes: { [key: string]: chrome.storage.StorageChange }) => void
  ): void {
    chrome.storage?.onChanged.addListener((changes, namespace) => {
      if (namespace === "local") {
        // 过滤出缓存键的变化
        const cacheChanges: { [key: string]: chrome.storage.StorageChange } =
          {};

        for (const key in changes) {
          if (this.isCacheItem(changes[key].newValue)) {
            cacheChanges[key] = changes[key];
          }
        }
        if (Object.keys(cacheChanges).length > 0) {
          callback(cacheChanges);
        }
      }
    });
  }

  /**
   * 计算数据大小
   */
  private static calculateSize(data: any): number {
    const jsonString = JSON.stringify(data);
    return new Blob([jsonString]).size;
  }
}

// ChromeCache.setMultiple([
//   {
//     key: "ExcludedDomainList",
//     data: {
//       code: 200,
//       data: [],
//       msg: "success",
//       // firstName: "John",
//       // lastName: "Doe",
//       // profileUrl: "https://www.linkedin.com/in/johndoe",
//       // msg: "success",
//       // data:[],
//     },
//   },
//   {
//     key: "lp_123",
//     data: {
//       // code: 200,
//       firstName: "John",
//       lastName: "Doe",
//       profileUrl: "https://www.linkedin.com/in/johndoe",
//       // msg: "success",
//       // data:[],
//     },
//   },
// ]);

// ChromeCache.set("ExcludedDomainList", "123");

// demo
// ChromeCache.get("lp_123").then((res) => {
//   console.log(res);
// });

// ChromeCache.get("ExcludedDomainList").then((res) => {
//   console.log(res);
// });

// ChromeCache.getMultiple(["lp_123", "ExcludedDomainList"]).then(
//   (res) => {
//     const a = res["lp_123"];
//     console.log(a?.firstName);
//     const b = res["ExcludedDomainList"];
//     console.log(b?.data);
//   }
// );
